import PrerenderReady from '../../components/PrerenderReady';
import CourseSeo from '../../components/course/CourseSeo';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog, { centsToExactDisplay } from '../../hooks/useCourseCatalog';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating, { TRUSTPILOT_URL } from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PromoCountdown from '../../components/course/PromoCountdown';
import PlanSelector, { orderPaymentPlans } from '../../components/course/PlanSelector';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import CurriculumAccordion from '../../components/course/CurriculumAccordion';
import BootcampTrailer from '../../components/course/BootcampTrailer';
import CourseHeroVisual from '../../components/course/CourseHeroVisual';
import PainPoints from '../../components/course/PainPoints';
import ScrollProgress from '../../components/course/ScrollProgress';
import CourseLegalDisclaimer from '../../components/course/CourseLegalDisclaimer';
import SpotChatWidget from '../../components/SpotChatWidget';
import { LIVE_COURSE_PATH as LIVE_PATH } from '../../components/course/membershipPaths';
import {
  ROB_STORY,
  NO_CODING_BADGE,
  REVIEW_SHOTS,
  VIDEO_REVIEWS,
  GUARANTEE_HEADLINE,
} from '../../components/course/courseContent';
import './CoursePage.css';

const { courseStructuredData } = require('../../site/structuredData');
const { getPublicPageSeo } = require('../../site/publicPageSeo');
const SELF_PACED_PATH = '/courses/ai-trading-bootcamp/self-paced';

export const LIVE_COURSE_PATH = LIVE_PATH;

/**
 * AI Trading Bootcamp Membership sales page (Rob, 2026-10-28).
 *
 * WHY THIS EXISTS: the yearly Membership (catalog product
 * `ai-bootcamp-self-paced`) is BotSpot's main offer. It bundles every
 * AI Trading Bootcamp recording, twelve months of BotSpot Pro, the members-only
 * weekly live class "Tuesday Sessions with Rob" and the
 * 30-day money-back guarantee. Rob approved reusing this existing On Demand
 * route instead of adding a new page.
 *
 * DATA RULES: the product name, every price, the Pro monthly price and the
 * live Bootcamp tuition come from the checkout catalog. Value anchors render
 * only when the catalog supplies the real number. Nothing is typed in and
 * nothing falls back to a guessed price.
 */

export const MEMBERSHIP_FAQS = [
  {
    q: 'Do I keep the recordings?',
    a: 'Yes. Every Bootcamp recording is yours to keep. They stay in your course portal even after your 12 months end.',
  },
  {
    q: 'What happens after 12 months?',
    a: 'You keep every recording. BotSpot Pro and Tuesday Sessions with Rob end at that point unless you renew.',
  },
  {
    q: 'How is this different from monthly Pro?',
    a: 'Monthly Pro is the BotSpot software only. The Membership gives you a full year of Pro plus every Bootcamp recording, the weekly live class with Rob, and four hours of one-on-one phone time with Rob.',
  },
  {
    q: 'What are Tuesday Sessions with Rob?',
    a: 'A live class with Rob every Tuesday, just for members. Bring your questions, show your setup and watch Rob build and trade with AI live. Every session is recorded.',
  },
  {
    q: 'Can AI really trade for me with real money?',
    a: 'Yes. You connect your own broker, set the rules and choose the account. Your AI agent researches and places trades for you inside those rules, and you can stop it any time. Trading has risk and we never promise profits.',
  },
  {
    q: 'Do I need to know how to code?',
    a: 'No. You tell Claude, Codex and BotSpot what you want in plain English.',
  },
  {
    q: 'Is there a guarantee?',
    a: 'Yes. You get a 30-day money-back guarantee.',
  },
  {
    q: 'What about the live Bootcamp?',
    a: 'The live Bootcamp runs a few times a year. Its page shows the next live class when dates are set. Members do not have to wait: Tuesday Sessions with Rob run every week.',
  },
];

const PAIN_LEDE =
  'We searched Reddit and X for the trading problems people complain about most. The Bootcamp recordings show you how AI fixes each one, and Tuesday Sessions help you when you get stuck.';
const PAIN_TURN =
  'Problem, then practice: Claude, Codex, Cursor, BotSpot, and your broker working together, with real money under your control.';

const TUESDAY_POINTS = [
  'Live on Zoom every Tuesday, for members only.',
  'Ask your own questions and show Rob your setup.',
  'Watch Rob research, build and trade with AI live.',
  'Every session is recorded, so you can catch up any time.',
];

/** "$219/mo for 5 months" or "$997". One number, always with its term. */
export const planPriceLine = (plan) => {
  if (!plan?.exactDisplay) return null;
  return plan.installments > 1
    ? `${plan.exactDisplay}/mo for ${plan.installments} months`
    : `${plan.exactDisplay} one payment`;
};

const CourseSelfPacedPage = () => {
  const { user } = useUser();
  const { startCheckout } = useCheckoutRedirect('course_self_paced');
  const {
    isLoading,
    selfPaced,
    selfPacedPlans = [],
    proMonthlyPlan,
    proAnnualPlan,
    liveStandardTuitionCents,
    sessions = [],
    pricingError,
    promoEndsAt,
    promoTierName,
  } = useCourseCatalog();

  const membershipName = selfPaced?.productName || null;
  // The monthly plan leads every price line (Rob, 2026-10-22). Pay in full is
  // offered in the plan picker. Use that same order for the default so adding
  // a shorter installment plan cannot displace the longest monthly option.
  const leadPlan = useMemo(() => orderPaymentPlans(selfPacedPlans)[0] || null, [selfPacedPlans]);
  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  useEffect(() => {
    if (!selectedPlanKey && leadPlan?.planKey) setSelectedPlanKey(leadPlan.planKey);
  }, [leadPlan, selectedPlanKey]);
  const selectedPlan =
    selfPacedPlans.find((plan) => plan.planKey === selectedPlanKey) || leadPlan || null;

  const prerenderReady = !isLoading && !pricingError && Boolean(selfPaced?.planKey);
  const courseSchema = prerenderReady
    ? courseStructuredData({
        path: SELF_PACED_PATH,
        name: membershipName || 'AI Trading Bootcamp Membership',
        description: getPublicPageSeo(SELF_PACED_PATH)?.description,
        plans: selfPacedPlans.length ? selfPacedPlans : [selfPaced],
        isEnrollable: true,
      })
    : null;

  // Value anchors come only from real catalog prices.
  const tuitionDisplay = centsToExactDisplay(liveStandardTuitionCents);
  const proYearCents =
    proAnnualPlan && Number.isFinite(proAnnualPlan.amount) ? proAnnualPlan.amount : null;
  const proYearDisplay = centsToExactDisplay(proYearCents);
  const totalValueDisplay =
    liveStandardTuitionCents && proYearCents
      ? centsToExactDisplay(liveStandardTuitionCents + proYearCents)
      : null;

  const promoActive = Boolean(promoEndsAt && leadPlan?.hasDiscount);
  const leadPriceLine = planPriceLine(leadPlan);

  const track = useCallback(
    (event, extra = {}) => {
      pushEvent({
        event,
        source: window.location.pathname,
        userId: user?.id || user?.sub,
        email: user?.email,
        ...extra,
      });
    },
    [user],
  );

  const buy = useCallback(
    (ctaLocation) => {
      const planKey = selectedPlan?.planKey || null;
      track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, {
        cta_location: ctaLocation,
        path: 'membership',
        productSlug: 'ai-bootcamp-self-paced',
        planKey,
      });
      return startCheckout({
        products: [{ productSlug: 'ai-bootcamp-self-paced', planKey }],
        flowContext: 'course_self_paced',
        metadata: {
          origin: {
            flow: 'course_self_paced',
            ctaLocation,
            returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
          },
        },
        contact: { email: user?.email || '', name: user?.name || '' },
      });
    },
    [startCheckout, track, selectedPlan, user],
  );

  const buyDisabled = isLoading || Boolean(pricingError) || !selectedPlan;

  const facts = useMemo(
    () => [
      { value: '12 months', label: 'BotSpot Pro included' },
      { value: 'Every Tuesday', label: 'Live class with Rob' },
      { value: 'Yours to keep', label: 'Bootcamp recordings' },
    ],
    [],
  );

  const offerStack = [
    {
      img: '/images/courses/inc-recordings.webp',
      name: 'Every AI Trading Bootcamp recording',
      detail:
        'The full Bootcamp, recorded live with Rob. New live classes are added as they run. Yours to keep.',
      value: tuitionDisplay ? `The same classes live students pay ${tuitionDisplay} for` : null,
      testId: 'offer-recordings',
    },
    {
      img: '/images/courses/inc-pro-month.webp',
      name: '12 months of BotSpot Pro',
      detail:
        'The AI trading software. Your AI agent researches, backtests and trades your own broker account every day, with real money and inside your rules.',
      value: proYearDisplay ? `${proYearDisplay} on the annual Pro plan` : null,
      testId: 'offer-pro',
    },
    {
      img: '/images/courses/inc-algo-lab.webp',
      name: 'Tuesday Sessions with Rob',
      detail: 'A live class every week, only for members. Get unstuck and see what is working now.',
      value: 'Members only',
      testId: 'offer-tuesday',
    },
    {
      img: '/images/courses/inc-one-on-one.webp',
      name: 'Four hours of one-on-one phone time with Rob',
      detail: 'Book phone time with Rob to work through your questions one-on-one.',
      value: null,
      testId: 'offer-coaching',
    },
    {
      img: '/images/courses/inc-guarantee.webp',
      name: GUARANTEE_HEADLINE,
      detail:
        'Try it for 30 days. If it is not for you, ask for your money back. The recordings are yours to keep.',
      value: null,
      testId: 'offer-guarantee',
    },
  ];

  const comparisonRows = [
    { feature: 'BotSpot Pro AI trading software', pro: true, member: true },
    {
      feature: 'Four hours of one-on-one phone time with Rob',
      pro: false,
      member: true,
    },
    {
      feature: 'Every AI Trading Bootcamp recording, yours to keep',
      pro: false,
      member: true,
    },
    {
      feature: 'Tuesday Sessions with Rob, live every week',
      pro: false,
      member: true,
    },
    { feature: '30-day money-back guarantee', pro: false, member: true },
  ];

  const priceBlock = (testId) =>
    pricingError || (!isLoading && !leadPlan) ? (
      <p className="cp-price-error" data-testid="pricing-error" role="alert">
        Pricing is temporarily unavailable. Please refresh, or contact us and we will help you join.
      </p>
    ) : (
      <div className="cp-hero-price" data-testid={testId}>
        <div className="cp-price-row">
          {leadPlan?.hasDiscount && leadPlan.originalExactDisplay ? (
            <s className="cp-price-was" data-testid="self-paced-was-price">
              {leadPlan.originalExactDisplay}
            </s>
          ) : null}
          <strong className="cp-price-now">{leadPlan?.exactDisplay || ''}</strong>
          {leadPlan ? (
            <span className="cp-price-unit">
              {leadPlan.installments > 1
                ? `/mo for ${leadPlan.installments} months`
                : 'one payment'}
            </span>
          ) : null}
        </div>
      </div>
    );

  return (
    <div className="course-page" data-testid="self-paced-page">
      <PrerenderReady
        ready={prerenderReady}
        state={
          isLoading
            ? 'loading'
            : pricingError
              ? 'error'
              : selfPaced?.planKey
                ? 'catalog'
                : 'unavailable'
        }
      />
      <ScrollProgress />
      <CourseSeo path={SELF_PACED_PATH} />
      <Helmet>
        {courseSchema ? (
          <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
        ) : null}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: MEMBERSHIP_FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      {promoActive && (
        <div className="cp-promobar" data-testid="promo-bar">
          <div className="cp-promobar-copy">
            <span className="cp-promobar-title">
              {promoTierName || 'Current pricing'}
              {leadPlan.originalExactDisplay && leadPriceLine ? (
                <>
                  {' · '}
                  <s className="cp-promobar-was" data-testid="promo-was-price">
                    was {leadPlan.originalExactDisplay}
                  </s>
                  {', now '}
                  <span data-testid="promo-now-price">{leadPriceLine}</span>
                </>
              ) : null}
            </span>
            <PromoCountdown
              endsAt={promoEndsAt}
              label={`${promoTierName || 'Current pricing'} ends in`}
              variant="blocks"
            />
          </div>
        </div>
      )}

      <header className="cp-hero">
        <div className="cp-hero-fx" aria-hidden="true">
          <div className="cp-hero-fx-grid" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--amber" />
          <div className="cp-hero-fx-orb cp-hero-fx-orb--blue" />
        </div>
        <div className="cp-hero-copy">
          <Reveal>
            <p className="cp-eyebrow">Yearly membership · Start today</p>
          </Reveal>
          <Reveal delay={60}>
            <h1 data-testid="membership-name">{membershipName || ''}</h1>
            <p className="cp-membership-byline" data-testid="membership-byline">
              by BotSpot
            </p>
          </Reveal>
          <Reveal delay={120}>
            <p className="cp-sub cp-sub--keep" data-testid="membership-promise">
              AI that researches and trades for you every day, with real money. Learn it step by
              step in the full AI Trading Bootcamp, with live help from Rob every week.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="cp-hero-rating">
              <StarRating rating={4.6} />
            </div>
          </Reveal>

          <Reveal delay={240}>
            {/* Never shows an invented price. */}
            {priceBlock('self-paced-price')}
          </Reveal>

          <Reveal delay={300}>
            <div className="cp-hero-cta">
              <button
                type="button"
                className="cp-btn-primary"
                data-testid="buy-self-paced"
                onClick={() => buy('hero')}
                disabled={buyDisabled}
                aria-busy={isLoading || undefined}
              >
                <span className="cp-btn-label">Join the Membership</span>
              </button>
              <a className="cp-btn-ghost" href="#everything-you-get">
                See Everything You Get
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <CourseHeroVisual />
          <p className="cp-evidence-caption">
            See how a plain-language idea moves through review, approval, and monitoring.
          </p>
        </Reveal>
      </header>

      <BootcampTrailer
        onPlay={() =>
          track(ANALYTICS_EVENTS.BOOTCAMP_VIDEO_STARTED, {
            cta_location: 'trailer',
            path: 'membership',
          })
        }
      />
      <LogoRow />
      <FactStrip facts={facts} />

      <PainPoints lede={PAIN_LEDE} turn={PAIN_TURN} />

      <section
        className="cp-section cp-included"
        id="everything-you-get"
        data-testid="membership-offer-stack"
      >
        <Reveal>
          <h2>Everything you get</h2>
        </Reveal>
        <ul className="cp-included-list">
          {offerStack.map((item, index) => (
            <Reveal as="li" key={item.name} delay={index * 55}>
              <img
                className="cp-included-img"
                src={item.img}
                alt=""
                loading="lazy"
                width="512"
                height="512"
              />
              <div data-testid={item.testId}>
                <h3>{item.name}</h3>
                <p>{item.detail}</p>
                {item.value ? (
                  <p className="cp-included-value" data-testid={`${item.testId}-value`}>
                    {item.value}
                  </p>
                ) : null}
              </div>
            </Reveal>
          ))}
        </ul>
        {totalValueDisplay && leadPriceLine ? (
          <Reveal delay={300}>
            <p className="cp-included-total" data-testid="membership-value-total">
              Live tuition plus annual Pro comparison: <strong>{totalValueDisplay}</strong>.
              Recordings are included. Live cohort seat sold separately. Members pay{' '}
              <strong>{leadPriceLine}</strong>.
            </p>
          </Reveal>
        ) : null}
      </section>

      <section className="cp-section cp-about" data-testid="tuesday-sessions">
        <Reveal>
          <h2>Tuesday Sessions with Rob</h2>
        </Reveal>
        <div className="cp-about-layout">
          <Reveal delay={40} className="cp-about-visual">
            <img
              src="/images/revenue/live-algo-lab-hero.webp"
              alt="A live Tuesday session with code, a trading chart and member questions"
              width="1024"
              height="572"
              loading="lazy"
            />
          </Reveal>
          <Reveal delay={80} className="cp-about-copy">
            <div className="cp-about-body">
              <p>
                Every Tuesday, Rob goes live with members. It is a working class, not a lecture. You
                bring the questions, and Rob shows you how to get your AI agent doing the work.
              </p>
              <ul className="cp-tuesday-list">
                {TUESDAY_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="cp-section" data-testid="membership-comparison">
        <Reveal>
          <h2>Monthly Pro or the Membership?</h2>
        </Reveal>
        <Reveal delay={80}>
          <div className="cp-compare-wrap">
            <table className="cp-compare">
              <thead>
                <tr>
                  <th scope="col">What you get</th>
                  <th scope="col" className="cp-col-live">
                    Membership <span>best value</span>
                  </th>
                  <th scope="col">Monthly Pro</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td className="cp-col-live">
                      {row.member ? (
                        <span className="cp-yes">Yes</span>
                      ) : (
                        <span className="cp-no">No</span>
                      )}
                    </td>
                    <td>
                      {row.pro ? (
                        <span className="cp-yes">Yes</span>
                      ) : (
                        <span className="cp-no">No</span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr data-testid="comparison-price-row">
                  <td>Price</td>
                  <td className="cp-col-live" data-testid="comparison-member-price">
                    {leadPriceLine || ''}
                  </td>
                  <td data-testid="comparison-pro-price">
                    {proMonthlyPlan?.exactDisplay
                      ? `${proMonthlyPlan.exactDisplay} every month`
                      : ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <p className="cp-note" data-testid="membership-comparison-note">
            {leadPlan?.installments > 1 && proMonthlyPlan?.exactDisplay
              ? `${leadPlan.installments} payments of ${leadPlan.exactDisplay} gets you the Bootcamp, Tuesdays and a full year of Pro. Pro alone is ${proMonthlyPlan.exactDisplay} every month.`
              : 'Monthly Pro is the software only. The Membership adds the full Bootcamp, the weekly live class and four hours of phone time with Rob, with a year of Pro included.'}
          </p>
        </Reveal>
      </section>

      <Reveal>
        <div className="cp-cta-band">
          <p>Get the full Bootcamp, a year of Pro and live Tuesdays with Rob.</p>
          <button
            type="button"
            className="cp-btn-primary"
            data-testid="buy-self-paced-mid"
            onClick={() => buy('after_comparison')}
            disabled={buyDisabled}
          >
            Join the Membership
          </button>
        </div>
      </Reveal>

      <section className="cp-section cp-curriculum" id="curriculum" data-testid="curriculum">
        <Reveal>
          <h2>Bootcamp curriculum</h2>
        </Reveal>
        {sessions.length ? (
          <div data-testid="curriculum-live-cohort">
            <CurriculumAccordion
              sessions={sessions}
              formatDate={null}
              showDuration={false}
              sessionNoun="AI Trading Bootcamp classes"
              metaNote="recorded live and added to your portal as each class runs"
            />
          </div>
        ) : (
          <p className="cp-muted" data-testid="curriculum-unavailable" role="status">
            The Bootcamp curriculum will appear here when class details are available.
          </p>
        )}
      </section>

      <section className="cp-section cp-instructor" aria-labelledby="instructor-heading">
        <Reveal>
          <h2 id="instructor-heading">{ROB_STORY.heading}</h2>
          <p className="cp-instructor-lede">
            Rob teaches the Bootcamp and runs every Tuesday Session.
          </p>
        </Reveal>
        <Reveal delay={70}>
          <div className="cp-instructor-body">
            <aside className="cp-instructor-aside">
              <img
                src="/images/rob.jpg"
                alt="Rob Grzesik"
                width="220"
                height="220"
                decoding="async"
              />
              <ul className="cp-instructor-creds">
                {ROB_STORY.credentials.map((cred) => (
                  <li key={cred.label}>
                    <strong>{cred.value}</strong>
                    <span>{cred.label}</span>
                  </li>
                ))}
              </ul>
            </aside>
            <div className="cp-instructor-copy">
              <h3>Rob Grzesik</h3>
              <p className="cp-instructor-role">{ROB_STORY.roleLine}</p>
              {ROB_STORY.paragraphs.map((para) => (
                <p key={para.slice(0, 48)}>{para}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-reviews" data-testid="student-reviews">
        <Reveal>
          <div className="cp-reviews-head">
            <h2>Student reviews</h2>
            <StarRating rating={4.6} compact />
          </div>
        </Reveal>
        <Reveal delay={40}>
          <p className="cp-reviews-lede">Hear from students who took the course.</p>
        </Reveal>
        <div className="cp-video-reviews" data-testid="video-reviews">
          {VIDEO_REVIEWS.map((review, index) => (
            <Reveal key={review.youtubeId} delay={index * 50} className="cp-video-review">
              <div className="cp-video-frame">
                <iframe
                  title={review.title}
                  src={`https://www.youtube-nocookie.com/embed/${review.youtubeId}?rel=0`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="cp-video-caption">{review.name}</p>
            </Reveal>
          ))}
        </div>
        <div className="cp-reviews-wall">
          {REVIEW_SHOTS.map((shot, index) => (
            <Reveal key={shot.src} delay={index * 60} className="cp-review-shot">
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ '--tilt': `${shot.tilt}deg` }}
              >
                <img src={shot.src} alt={shot.alt} loading="lazy" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={380}>
          <p className="cp-reviews-more">
            <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
              Read all reviews on Trustpilot
            </a>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-pricing" id="pricing" data-testid="self-paced-pricing">
        <Reveal>
          <h2>Join the Membership</h2>
        </Reveal>
        <Reveal delay={80}>
          <div className="cp-pricing-card">
            {promoActive ? (
              <div className="cp-pricing-promo">
                <p className="cp-pricing-promo-note">{promoTierName || 'Current pricing'}</p>
                <PromoCountdown
                  endsAt={promoEndsAt}
                  label={`${promoTierName || 'Current pricing'} ends in`}
                  variant="blocks"
                />
              </div>
            ) : null}
            {pricingError || (!isLoading && !selfPacedPlans.length) ? (
              <p className="cp-price-error" role="alert">
                We could not load current pricing. Rather than show you a price that might be wrong,
                we would rather say so. Please refresh, or contact us and we will get you started.
              </p>
            ) : (
              <div data-testid="membership-plan-picker">
                <PlanSelector
                  plans={selfPacedPlans}
                  selectedKey={selectedPlan?.planKey || null}
                  onSelect={setSelectedPlanKey}
                />
              </div>
            )}
            <button
              type="button"
              className="cp-btn-primary cp-btn-block"
              data-testid="buy-self-paced-pricing"
              onClick={() => buy('pricing')}
              disabled={buyDisabled}
            >
              Join the Membership
            </button>
            <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
            <GuaranteeBlock
              compact
              description="30-day money-back guarantee. The recordings are yours to keep."
            />
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-faq" data-testid="membership-faq">
        <Reveal>
          <h2>Questions people ask</h2>
        </Reveal>
        <dl>
          {MEMBERSHIP_FAQS.map((f, i) => (
            <Reveal as="div" key={f.q} delay={i * 40}>
              <dt>{f.q}</dt>
              <dd>{f.a}</dd>
            </Reveal>
          ))}
        </dl>
        <Reveal>
          <p className="cp-note">
            Want the live class schedule? <Link to={LIVE_PATH}>See the live Bootcamp page</Link>.
          </p>
        </Reveal>
      </section>

      <section
        className="cp-section cp-final"
        style={{
          backgroundImage:
            'linear-gradient(rgba(6,11,22,0.72), rgba(6,11,22,0.88)), ' +
            'url("/images/courses/final-cta-bg.webp")',
        }}
      >
        <Reveal>
          <h2>Start today</h2>
          <p>Learn it, run it with real money, and get live help every Tuesday.</p>
          <div className="cp-final-actions">
            <button
              type="button"
              className="cp-btn-primary"
              data-testid="buy-self-paced-final"
              onClick={() => buy('final')}
              disabled={buyDisabled}
            >
              <span className="cp-btn-label">Join the Membership</span>
            </button>
          </div>
          <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
        </Reveal>
      </section>

      <div className="cp-sticky">
        <span className="cp-sticky-price" data-testid="sticky-price">
          {isLoading ? '' : pricingError || !leadPlan ? 'Pricing unavailable' : leadPriceLine}
        </span>
        <button
          type="button"
          className="cp-btn-primary"
          data-testid="buy-self-paced-sticky"
          onClick={() => buy('sticky')}
          disabled={buyDisabled}
        >
          Join Now
        </button>
      </div>

      <CourseLegalDisclaimer subject="This material" />
      {/* The portal preserves the shared launcher's fixed position and styles. */}
      {createPortal(<SpotChatWidget />, document.body)}
    </div>
  );
};

export default CourseSelfPacedPage;
